/**
 * Institution Configuration & Terminology System
 * 
 * CRITICAL TENANT RULE:
 * institutionType is an Organization/Tenant property, NOT a global application setting.
 * All terminology, dashboard metrics, and sidebar modules are dynamically derived
 * from the authenticated organization (orgData).
 */

export const INSTITUTION_TYPES = {
  SCHOOL: 'SCHOOL',
  PUC: 'PUC',
  DEGREE_COLLEGE: 'DEGREE_COLLEGE'
};

/**
 * Normalizes institution type from orgData.
 * Defaults safely to 'SCHOOL' for backwards compatibility if null/blank.
 * 
 * @param {Object|string} orgData - Logged in organization details or institutionType string
 * @returns {'SCHOOL' | 'PUC' | 'DEGREE_COLLEGE'} Normalized institution type
 */
export const getNormalizedInstitutionType = (orgData) => {
  if (!orgData) return INSTITUTION_TYPES.SCHOOL;

  let rawType = '';
  if (typeof orgData === 'string') {
    rawType = orgData.toUpperCase().trim();
  } else {
    rawType = (orgData.institutionType || orgData.institution_type || orgData.type || '').toUpperCase().trim();
  }
  
  if (rawType === 'PUC' || rawType === 'PUC_COLLEGE' || rawType === 'PRE_UNIVERSITY') {
    return INSTITUTION_TYPES.PUC;
  }

  if (
    rawType === 'DEGREE_COLLEGE' ||
    rawType === 'COLLEGE' ||
    rawType === 'UNIVERSITY' ||
    rawType === 'ENGINEERING_COLLEGE'
  ) {
    return INSTITUTION_TYPES.DEGREE_COLLEGE;
  }

  if (rawType === 'SCHOOL') {
    return INSTITUTION_TYPES.SCHOOL;
  }

  // Check orgId or orgName heuristics as fallback if institutionType is not explicitly set
  if (typeof orgData === 'object') {
    const orgIdUpper = (orgData.orgId || '').toUpperCase();
    const orgNameUpper = (orgData.orgName || orgData.institutionName || '').toUpperCase();

    if (orgIdUpper.startsWith('PUC') || orgNameUpper.includes('PUC') || orgNameUpper.includes('PRE-UNIVERSITY')) {
      return INSTITUTION_TYPES.PUC;
    }

    if (
      orgIdUpper.startsWith('COL') || 
      orgIdUpper.startsWith('GDC') || 
      orgNameUpper.includes('COLLEGE') || 
      orgNameUpper.includes('DEGREE') ||
      orgNameUpper.includes('UNIVERSITY')
    ) {
      return INSTITUTION_TYPES.DEGREE_COLLEGE;
    }
  }

  // Safe default
  return INSTITUTION_TYPES.SCHOOL;
};

export const isDegreeCollege = (orgData) => {
  return getNormalizedInstitutionType(orgData) === INSTITUTION_TYPES.DEGREE_COLLEGE;
};

export const isPuc = (orgData) => {
  return getNormalizedInstitutionType(orgData) === INSTITUTION_TYPES.PUC;
};

export const isSchool = (orgData) => {
  return getNormalizedInstitutionType(orgData) === INSTITUTION_TYPES.SCHOOL;
};

/**
 * Terminology configuration per institution type
 */
export const TERMINOLOGY = {
  SCHOOL: {
    institutionLabel: 'School',
    studentLabel: 'Students',
    studentSingular: 'Student',
    teacherLabel: 'Teachers',
    teacherSingular: 'Teacher',
    classLabel: 'Classes',
    classSingular: 'Class',
    sectionLabel: 'Sections',
    sectionSingular: 'Section',
    subjectLabel: 'Subjects',
    subjectSingular: 'Subject',
    academicYearLabel: 'Academic Year',
    attendanceLabel: 'School Attendance',
    feeLabel: 'School Fees',
    examLabel: 'Examinations & Tests',
    marksLabel: 'Marks Cards',
    timetableLabel: 'Timetable',
    homeworkLabel: 'Homework / Assignments',
    academicModuleLabel: 'Classes',
    hierarchyDescription: 'Academic Year → Class → Section → Subjects → Class Teacher'
  },
  PUC: {
    institutionLabel: 'PUC College',
    studentLabel: 'Students',
    studentSingular: 'Student',
    teacherLabel: 'Lecturers / Teachers',
    teacherSingular: 'Lecturer',
    streamLabel: 'Streams',
    streamSingular: 'Stream',
    yearLabel: 'PUC Years',
    yearSingular: 'PUC Year',
    sectionLabel: 'Sections',
    sectionSingular: 'Section',
    subjectLabel: 'Subjects',
    subjectSingular: 'Subject',
    academicYearLabel: 'Academic Year',
    attendanceLabel: 'PUC Attendance',
    feeLabel: 'Annual Fees',
    examLabel: 'Examinations & Unit Tests',
    marksLabel: 'Results & Marks Cards',
    timetableLabel: 'Timetable',
    homeworkLabel: 'Assignments & Practicals',
    academicModuleLabel: 'Streams & Sections',
    hierarchyDescription: 'Academic Year → Stream → PUC Year (1st/2nd PUC) → Section → Subjects'
  },
  DEGREE_COLLEGE: {
    institutionLabel: 'Degree College',
    studentLabel: 'Students',
    studentSingular: 'Student',
    teacherLabel: 'Faculty',
    teacherSingular: 'Faculty Member',
    departmentLabel: 'Departments',
    departmentSingular: 'Department',
    programLabel: 'Programs / Courses',
    programSingular: 'Program',
    semesterLabel: 'Semesters',
    semesterSingular: 'Semester',
    sectionLabel: 'Sections',
    sectionSingular: 'Section',
    subjectLabel: 'Subjects',
    subjectSingular: 'Subject',
    advisorLabel: 'Faculty Advisors',
    advisorSingular: 'Faculty Advisor',
    academicYearLabel: 'Academic Year',
    attendanceLabel: 'College Attendance',
    feeLabel: 'Semester Fees',
    examLabel: 'Examinations',
    marksLabel: 'Grades & Results',
    timetableLabel: 'Schedule & Timetable',
    homeworkLabel: 'Assignments & Projects',
    academicModuleLabel: 'Academic Structure',
    hierarchyDescription: 'Academic Year → Department → Program → Semester → Section → Subjects → Faculty Advisor'
  }
};

/**
 * Returns the terminology object for a given organization or institution type.
 */
export const getInstitutionTerminology = (orgOrType) => {
  const type = getNormalizedInstitutionType(orgOrType);
  return TERMINOLOGY[type] || TERMINOLOGY.SCHOOL;
};

/**
 * Dashboard configuration for metric summary cards and layouts.
 */
export const DASHBOARD_CONFIG = {
  SCHOOL: {
    type: 'SCHOOL',
    title: 'School Administration Dashboard',
    summaryCards: [
      { key: 'totalStudents', label: 'Total Students', icon: '👨‍🎓', bg: '#eff6ff', border: '#bfdbfe' },
      { key: 'activeStudents', label: 'Active Students', icon: '✅', bg: '#f0fdf4', border: '#bbf7d0' },
      { key: 'totalTeachers', label: 'Teachers', icon: '👩‍🏫', bg: '#faf5ff', border: '#e9d5ff' },
      { key: 'totalClasses', label: 'Classes', icon: '🏫', bg: '#fdf2f8', border: '#fbcfe8' },
      { key: 'feeCollected', label: 'Fee Collected', icon: '💰', bg: '#fffbeb', border: '#fde68a', isCurrency: true },
      { key: 'pendingFees', label: 'Pending Fees', icon: '📝', bg: '#fff1f2', border: '#fecdd3', isCurrency: true },
      { key: 'totalSubjects', label: 'Subjects', icon: '📚', bg: '#f0fdfa', border: '#99f6e4' },
      { key: 'avgAttendance', label: 'Attendance', icon: '📊', bg: '#f5f3ff', border: '#ddd6fe', isPercentage: true }
    ],
    showClassDistribution: true,
    distributionTitle: 'Class-wise Student Distribution'
  },
  PUC: {
    type: 'PUC',
    title: 'PUC Administration Dashboard',
    summaryCards: [
      { key: 'totalStudents', label: 'Total Students', icon: '👨‍🎓', bg: '#eff6ff', border: '#bfdbfe' },
      { key: 'activeStudents', label: 'Active Students', icon: '✅', bg: '#f0fdf4', border: '#bbf7d0' },
      { key: 'totalTeachers', label: 'Lecturers', icon: '👩‍🏫', bg: '#faf5ff', border: '#e9d5ff' },
      { key: 'totalStreams', label: 'Streams', icon: '🔬', bg: '#fdf2f8', border: '#fbcfe8', defaultValue: '3' },
      { key: 'totalPucYears', label: 'PUC Years', icon: '🎓', bg: '#f0fdf4', border: '#bbf7d0', defaultValue: '2' },
      { key: 'feeCollected', label: 'Fee Collected', icon: '💰', bg: '#fffbeb', border: '#fde68a', isCurrency: true },
      { key: 'pendingFees', label: 'Pending Fees', icon: '📝', bg: '#fff1f2', border: '#fecdd3', isCurrency: true },
      { key: 'totalSubjects', label: 'Subjects', icon: '📚', bg: '#f0fdfa', border: '#99f6e4' }
    ],
    showClassDistribution: true,
    distributionTitle: 'Stream & Year Student Distribution'
  },
  DEGREE_COLLEGE: {
    type: 'DEGREE_COLLEGE',
    title: 'Degree College Administration Dashboard',
    summaryCards: [
      { key: 'totalStudents', label: 'Total Students', icon: '👨‍🎓', bg: '#eff6ff', border: '#bfdbfe' },
      { key: 'activeStudents', label: 'Active Students', icon: '✅', bg: '#f0fdf4', border: '#bbf7d0' },
      { key: 'totalTeachers', label: 'Faculty', icon: '👩‍🏫', bg: '#faf5ff', border: '#e9d5ff' },
      { key: 'totalDepartments', label: 'Departments', icon: '🏛️', bg: '#fdf2f8', border: '#fbcfe8' },
      { key: 'totalPrograms', label: 'Programs', icon: '🎓', bg: '#f0fdf4', border: '#bbf7d0' },
      { key: 'feeCollected', label: 'Fee Collected', icon: '💰', bg: '#fffbeb', border: '#fde68a', isCurrency: true },
      { key: 'pendingFees', label: 'Pending Fees', icon: '📝', bg: '#fff1f2', border: '#fecdd3', isCurrency: true },
      { key: 'currentAcademicYear', label: 'Academic Year', icon: '📅', bg: '#f5f3ff', border: '#ddd6fe', defaultValue: '2026-27' }
    ],
    showClassDistribution: false,
    distributionTitle: 'Department & Program Distribution'
  }
};

/**
 * Returns the dashboard configuration for a given organization or institution type.
 */
export const getDashboardConfig = (orgOrType) => {
  const type = getNormalizedInstitutionType(orgOrType);
  return DASHBOARD_CONFIG[type] || DASHBOARD_CONFIG.SCHOOL;
};
